import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useIsPermitted } from "@/utils/isPermitted";
import { PAGE_ROUTES } from "@/constants/PermissionsConstants";
import { CoachCustomization } from "@/types/CoachTypes";

interface CoachAccess {
  /** The client/group has the coach module switched on. */
  isModuleEnabled: boolean;
  /** Module on AND the user holds coach_access — mirrors the backend gate. */
  canUseCoach: boolean;
  /** Persona to display, resolved from the same customizations block. */
  persona: { name: string; avatar: string | null };
  /** Realtime voice coach (phase 2). */
  isVoiceEnabled: boolean;
  /**
   * May maintain their own Personalisation: the client AND the event both allow
   * it AND the user holds coach_personalisation. Mirrors the backend's
   * coachPersonalisationService — note this one is a conjunction, not an OR.
   */
  canMaintainPersonalisation: boolean;
}

const DEFAULT_PERSONA = { name: "Coach", avatar: null };

const readCoachBlock = (entity?: {
  customizations?: unknown;
}): CoachCustomization | null => {
  const customizations = entity?.customizations;
  if (
    typeof customizations !== "object" ||
    customizations === null ||
    Array.isArray(customizations)
  ) {
    return null;
  }
  const coach = (customizations as { coach?: unknown }).coach;
  if (typeof coach !== "object" || coach === null || Array.isArray(coach)) {
    return null;
  }
  return coach as CoachCustomization;
};

/**
 * Whether the coach module is available to the current user.
 *
 * The coach ships per client: the backend requires the coach_access permission
 * AND sf_clients/sf_groups customizations.coach.enabled. This hook applies the
 * same two rules on the client so the nav entry, dashboard widget and page all
 * appear only when the API would actually answer.
 *
 * `enabled` is an OR across the user's clients and groups (one enabled scope is
 * enough); persona fields cascade client -> group, with the group winning.
 *
 * Personalisation is the exception and is resolved as a CONJUNCTION — client AND
 * event — because it gates whose personal data reaches the model rather than
 * whether a module exists. The backend's coachPersonalisationService is the
 * authority and enforces it independently; this mirrors the rule so the screen is
 * never offered to someone whose save would be refused.
 */
export function useCoachEnabled(): CoachAccess {
  const { user } = useAuth();
  const isPermitted = useIsPermitted();
  // useIsPermitted returns a fresh closure each render, so resolve it to a
  // boolean before the memo — otherwise the dependency would change every time.
  const hasCoachPermission = isPermitted(PAGE_ROUTES.COACH);
  const hasPersonalisationPermission = isPermitted(PAGE_ROUTES.COACH_PERSONALISATION);

  return useMemo(() => {
    const clientBlocks = (user?.sf_clients ?? [])
      .map(readCoachBlock)
      .filter((block): block is CoachCustomization => block !== null);
    const groupBlocks = (user?.sf_groups ?? [])
      .map(readCoachBlock)
      .filter((block): block is CoachCustomization => block !== null);
    const blocks = [...clientBlocks, ...groupBlocks];

    const isModuleEnabled = blocks.some((block) => block.enabled === true);
    const isVoiceEnabled = blocks.some((block) => block.voiceEnabled === true);

    const persona = blocks.reduce(
      (acc, block) => ({
        name: block.personaName || acc.name,
        avatar: block.personaAvatar || acc.avatar,
      }),
      { ...DEFAULT_PERSONA } as { name: string; avatar: string | null },
    );

    // Client AND event, pairing each event with ITS OWN client — a user's
    // membership of some other enabled client must not rescue an event whose
    // client has switched personalisation off. An event with no stored value
    // inherits its client, so absent is not off; without that, switching a client
    // on would change nothing until every event had been edited by hand.
    const clientPersonalisation = new Map(
      (user?.sf_clients ?? []).map((client) => [
        client.id,
        readCoachBlock(client)?.personalisationEnabled,
      ]),
    );
    const events = user?.sf_groups ?? [];
    const isPersonalisationEnabled =
      events.length === 0
        ? [...clientPersonalisation.values()].some((value) => value === true)
        : events.some((event) => {
            if (clientPersonalisation.get(event.client_id) !== true) return false;
            const own = readCoachBlock(event)?.personalisationEnabled;
            return own === undefined ? true : own === true;
          });

    return {
      isModuleEnabled,
      isVoiceEnabled,
      persona,
      canUseCoach: isModuleEnabled && hasCoachPermission,
      canMaintainPersonalisation: isPersonalisationEnabled && hasPersonalisationPermission,
    };
  }, [user, hasCoachPermission, hasPersonalisationPermission]);
}
